import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import SmsBalanceApi, {
  CHARGE_AMOUNTS,
  SmsBalanceRes,
  SmsChargeHistoryRes,
} from "api/SmsBalanceApi";

const STATUS_LABELS: Record<SmsChargeHistoryRes["status"], string> = {
  pending: "支払い待ち",
  succeeded: "完了",
  failed: "失敗",
};
const STATUS_COLORS: Record<
  SmsChargeHistoryRes["status"],
  "default" | "primary" | "secondary"
> = {
  pending: "default",
  succeeded: "primary",
  failed: "secondary",
};

const SmsBalance: React.FC = () => {
  const companyId = useSelector(
    (state: { account: { staff: { companyId: number } } }) =>
      state.account.staff.companyId
  );
  const [balance, setBalance] = useState<SmsBalanceRes | null>(null);
  const [history, setHistory] = useState<SmsChargeHistoryRes[]>([]);
  const [loading, setLoading] = useState(false);
  const [charging, setCharging] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [b, h] = await Promise.all([
        SmsBalanceApi.find(companyId),
        SmsBalanceApi.history(companyId),
      ]);
      setBalance(b);
      setHistory(h);
    } catch (e) {
      console.error("残高取得失敗", e);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCharge = async (amount: number) => {
    if (!companyId) return;
    setCharging(amount);
    try {
      const baseUrl = window.location.origin + window.location.pathname;
      const result = await SmsBalanceApi.createCheckoutSession(companyId, {
        amount,
        successUrl: `${baseUrl}?charge=success`,
        cancelUrl: `${baseUrl}?charge=cancel`,
      });
      window.location.href = result.checkoutUrl;
    } catch (e) {
      const msg =
        e instanceof Error
          ? e.message
          : (e as { response?: { data?: { message?: string } } })?.response
              ?.data?.message ?? "チャージ開始に失敗しました";
      window.alert(msg);
      setCharging(null);
    }
  };

  const balanceValue = balance?.smsBalance ?? 0;
  const balanceColor =
    balanceValue >= 1000
      ? "#1b5e20"
      : balanceValue > 0
      ? "#ef6c00"
      : "#c62828";

  return (
    <Box padding={3}>
      <Typography variant="h5" gutterBottom>
        SMS デポジット残高
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        visit 業態の受注画面で SMS 送信時に消費される残高 (1 通あたり ¥15、 70 文字超過は 2 通分)。
        残高不足時は送信できないので、 事前にチャージしてください。
      </Typography>
      <Divider style={{ marginBottom: 20 }} />

      <Paper style={{ padding: 24, maxWidth: 720, marginBottom: 24 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="caption" color="textSecondary">
              現在の残高
            </Typography>
            <Typography
              variant="h3"
              style={{ color: balanceColor, fontWeight: "bold" }}
            >
              ¥{balanceValue.toLocaleString()}
            </Typography>
            {balanceValue < 1000 ? (
              <Chip
                label={
                  balanceValue === 0
                    ? "残高がありません。 チャージしてください"
                    : "残高がまもなく不足します"
                }
                size="small"
                style={{
                  marginTop: 8,
                  backgroundColor: balanceValue === 0 ? "#c62828" : "#ef6c00",
                  color: "white",
                }}
              />
            ) : null}
          </Box>
          {loading ? <CircularProgress size={32} /> : null}
        </Box>

        <Box mt={3}>
          <Typography variant="subtitle2" gutterBottom>
            チャージ金額を選んでください
          </Typography>
          <Box display="flex" style={{ gap: 12, flexWrap: "wrap" }}>
            {CHARGE_AMOUNTS.map((amount) => (
              <Button
                key={amount}
                variant="contained"
                color="primary"
                disabled={charging !== null}
                onClick={() => handleCharge(amount)}
                style={{ minWidth: 140 }}
              >
                {charging === amount ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  `¥${amount.toLocaleString()} チャージ`
                )}
              </Button>
            ))}
          </Box>
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ display: "block", marginTop: 8 }}
          >
            支払いは Stripe Checkout に遷移します。 完了後本ページに戻り、 残高が反映されます (Webhook 経由)。
          </Typography>
        </Box>
      </Paper>

      <Typography variant="h6" gutterBottom>
        チャージ履歴 (新しい順)
      </Typography>
      <Paper variant="outlined" style={{ maxWidth: 720 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>日時</TableCell>
              <TableCell align="right">金額</TableCell>
              <TableCell align="center">ステータス</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="body2" color="textSecondary">
                    まだチャージ履歴がありません
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              history.map((h) => (
                <TableRow key={h.smsChargeHistoryId}>
                  <TableCell>
                    {new Date(h.createdAt).toLocaleString("ja-JP")}
                  </TableCell>
                  <TableCell align="right">
                    ¥{Number(h.amount).toLocaleString()}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={STATUS_LABELS[h.status]}
                      size="small"
                      color={STATUS_COLORS[h.status]}
                      variant={h.status === "succeeded" ? "default" : "outlined"}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default SmsBalance;
